import Vue from 'vue'

export default {
  namespaced: true,
  state: {
    isOnboarding: false,
    currentStep: 1,
    totalSteps: 4,
    needsPages: null,
    needsUsers: null,
    selectedNetworks: [],
    selectedFeatures: [],
    connectedPages: {
      facebook: [],
      instagram: [],
      linkedin: [],
      twitter: [], // Twitter OAuth 2.0 (modern API)
      x: [], // Twitter OAuth 1.0 (legacy API)
      google: [],
      threads: [],
      tiktok: [],
      youtube: [],
      bluesky: [],
      pinterest: [],
      telegram: [],
      whatsapp: []
    },
    nativeConnections: {
      // Store native connection status per page
      // Format: { pageId: { connected: true, timestamp: Date } }
    },
    selectedPageIds: {
      // Store selected page IDs per network (max 10)
      // Format: { network: [pageId1, pageId2, ...] }
    },
    preExistingPageIds: {
      // Pages that existed BEFORE onboarding started
      // These should NEVER be disconnected, even if unselected
      // Format: { network: [pageId1, pageId2, ...] }
    },
    newlyConnectedPageIds: {
      // Pages connected DURING onboarding
      // These can be disconnected if unselected
      // Format: { network: [pageId1, pageId2, ...] }
    },
    isCompleted: false
  },
  mutations: {
    SET_NEEDS(state, { pages, users }) {
      state.needsPages = pages
      state.needsUsers = users
    },
    SET_IS_ONBOARDING(state, value) {
      state.isOnboarding = value
    },
    SET_STEP(state, step) {
      state.currentStep = step
    },
    SET_NETWORKS(state, networks) {
      state.selectedNetworks = networks
    },
    SET_FEATURES(state, features) {
      state.selectedFeatures = features
    },
    ADD_CONNECTED_PAGE(state, { network, page }) {
      if (!state.connectedPages[network]) {
        // Use Vue.set to ensure reactivity when initializing array
        Vue.set(state.connectedPages, network, [])
      }
      
      // Protection against duplicates: check if page already exists
      // Use network_id, id, AND name for comparison to catch all duplicate cases
      const pageIdentifier = page.id || page.network_id
      const alreadyExists = state.connectedPages[network].some(
        p => (p.id && p.id === page.id) ||
             (p.network_id && page.network_id && p.network_id === page.network_id) ||
             (p.name && page.name && p.name === page.name)
      )
      
      if (!alreadyExists) {
        // Push is reactive on existing arrays
        state.connectedPages[network].push(page)
        
        // Track as newly connected page (not pre-existing)
        if (!state.newlyConnectedPageIds[network]) {
          Vue.set(state.newlyConnectedPageIds, network, [])
        }
        if (pageIdentifier && !state.newlyConnectedPageIds[network].includes(pageIdentifier)) {
          state.newlyConnectedPageIds[network].push(pageIdentifier)
        }
        
        console.log(`[Onboarding Store] Added NEW page: ${page.name} (${pageIdentifier}) to ${network}`)
      } else {
        console.warn(`[Onboarding Store] Page ${page.name} (${pageIdentifier}) already exists in ${network}, skipping duplicate`)
      }
    },
    REMOVE_CONNECTED_PAGE(state, { network, pageId }) {
      if (state.connectedPages[network]) {
        // Find the page to get both its id and network_id
        const pageToRemove = state.connectedPages[network].find(
          p => p.id === pageId || p.network_id === pageId
        )
        
        // Filter out page by both id and network_id to handle all cases
        state.connectedPages[network] = state.connectedPages[network].filter(
          p => p.id !== pageId && p.network_id !== pageId
        )
        
        // If page was found, also filter by its network_id in other collections
        if (pageToRemove) {
          const idsToRemove = [pageId, pageToRemove.id, pageToRemove.network_id].filter(Boolean)
          
          // Clean up orphaned references using all possible IDs
          if (state.selectedPageIds[network]) {
            state.selectedPageIds[network] = state.selectedPageIds[network].filter(
              id => !idsToRemove.includes(id)
            )
          }
          if (state.preExistingPageIds[network]) {
            state.preExistingPageIds[network] = state.preExistingPageIds[network].filter(
              id => !idsToRemove.includes(id)
            )
          }
          if (state.newlyConnectedPageIds[network]) {
            state.newlyConnectedPageIds[network] = state.newlyConnectedPageIds[network].filter(
              id => !idsToRemove.includes(id)
            )
          }
          // Clean up native connections for all possible IDs
          idsToRemove.forEach(id => {
            if (state.nativeConnections[id]) {
              Vue.delete(state.nativeConnections, id)
            }
          })
        }
      }
    },
    UPDATE_PAGE(state, { network, pageId, updates }) {
      if (state.connectedPages[network]) {
        const pageIndex = state.connectedPages[network].findIndex(
          p => p.id === pageId
        )
        if (pageIndex !== -1) {
          // Use Vue.set to ensure reactivity when updating nested properties
          Object.keys(updates).forEach(key => {
            Vue.set(state.connectedPages[network][pageIndex], key, updates[key])
          })
          console.log(`[Onboarding Store] Updated page ${pageId} in ${network}:`, updates)
        }
      }
    },
    SET_SELECTED_PAGE_IDS(state, { network, pageIds }) {
      Vue.set(state.selectedPageIds, network, pageIds)
    },
    SET_ALL_SELECTED_PAGE_IDS(state, selectedPageIds) {
      state.selectedPageIds = selectedPageIds
    },
    SET_PRE_EXISTING_PAGE_IDS(state, { network, pageIds }) {
      if (!state.preExistingPageIds[network]) {
        Vue.set(state.preExistingPageIds, network, [])
      }
      state.preExistingPageIds[network] = pageIds
      console.log(`[Onboarding Store] Marked ${pageIds.length} pages as pre-existing for ${network}`)
    },
    SET_CONNECTED_PAGES(state, connectedPages) {
      // Deduplicate within each network to clean up duplicates from persisted state.
      // Duplicates occur when a pre-existing page (UUID id) is reconnected during
      // onboarding and added with network_id as id, then both are persisted to localStorage.
      Object.keys(connectedPages).forEach(network => {
        const pages = connectedPages[network]
        if (!Array.isArray(pages) || pages.length <= 1) return

        const uniqueMap = new Map()
        pages.forEach(page => {
          const key = page.network_id || page.name || page.id
          if (!key) return

          if (uniqueMap.has(key)) {
            const existing = uniqueMap.get(key)
            // Prefer the entry with a UUID-format id over network_id-format id
            const isUuid = (id) => typeof id === 'string' && /^[0-9a-f]{8}-/.test(id)
            if (isUuid(page.id) && !isUuid(existing.id)) {
              uniqueMap.set(key, page)
            }
          } else {
            uniqueMap.set(key, page)
          }
        })

        const deduped = Array.from(uniqueMap.values())
        if (deduped.length < pages.length) {
          console.log(`[Onboarding Store] Deduplicated ${network}: ${pages.length} → ${deduped.length} pages`)
          connectedPages[network] = deduped
        }
      })
      state.connectedPages = connectedPages
    },
    SET_NETWORK_PAGES(state, { network, pages }) {
      Vue.set(state.connectedPages, network, pages)
    },
    SET_NATIVE_CONNECTION(state, { network, pageId, connected, storyConfig }) {
      Vue.set(state.nativeConnections, pageId, {
        connected,
        network,
        storyConfig: storyConfig || null,
        timestamp: new Date().toISOString()
      })
    },
    // Bulk restore of all native connections (used when restoring from localStorage)
    SET_NATIVE_CONNECTIONS(state, nativeConnections) {
      state.nativeConnections = nativeConnections
    },
    // Bulk restore of newly connected page IDs (used when restoring from localStorage)
    SET_NEWLY_CONNECTED_PAGE_IDS(state, newlyConnectedPageIds) {
      state.newlyConnectedPageIds = newlyConnectedPageIds
    },
    COMPLETE_ONBOARDING(state) {
      state.isCompleted = true
    },
    RESET_ONBOARDING(state) {
      state.isOnboarding = false
      state.currentStep = 1
      state.needsPages = null
      state.needsUsers = null
      state.selectedNetworks = []
      state.selectedFeatures = []
      state.connectedPages = {
        facebook: [],
        instagram: [],
        linkedin: [],
        twitter: [],
        x: [],
        google: [],
        threads: [],
        tiktok: [],
        youtube: [],
        bluesky: [],
        pinterest: [],
        telegram: [],
        whatsapp: []
      }
      state.nativeConnections = {}
      state.selectedPageIds = {}
      state.preExistingPageIds = {}
      state.newlyConnectedPageIds = {}
      state.isCompleted = false
    }
  },
  actions: {
    async completeOnboarding({ commit, state }) {
      await this.dispatch('user/updateOnboardingPreferences', {
        networks: state.selectedNetworks,
        features: state.selectedFeatures
      })
      commit('COMPLETE_ONBOARDING')
      localStorage.setItem('onboardingCompleted', true)
    }
  },
  getters: {
    progress: state => (state.currentStep / state.totalSteps) * 100,
    canGoNext: state => {
      if (state.currentStep === 1) return state.selectedNetworks.length > 0
      if (state.currentStep === 2) return state.selectedFeatures.length > 0
      if (state.currentStep === 3) {
        const totalPages = Object.values(state.connectedPages).reduce(
          (sum, pages) => sum + pages.length,
          0
        )
        return totalPages > 0
      }
      return false
    },
    // Total CONNECTED pages (all pages, for navigation logic)
    totalConnectedPages: state => {
      return Object.values(state.connectedPages).reduce(
        (sum, pages) => sum + pages.length,
        0
      )
    },
    // Total SELECTED + PRE-EXISTING pages (for UI display)
    // Counts actual page objects that match selected or pre-existing IDs,
    // checking by both id and network_id to handle UUID vs network_id mismatches.
    totalSelectedPages: state => {
      let count = 0
      Object.keys(state.connectedPages).forEach(network => {
        const pages = state.connectedPages[network] || []
        const selectedIds = state.selectedPageIds[network] || []
        const preExistingIds = state.preExistingPageIds[network] || []
        pages.forEach(page => {
          const isSelected = selectedIds.includes(page.id) ||
            (page.network_id && selectedIds.includes(page.network_id))
          const isPreExisting = preExistingIds.includes(page.id) ||
            (page.network_id && preExistingIds.includes(page.network_id))
          if (isSelected || isPreExisting) {
            count++
          }
        })
      })
      return count
    },
    // Count CONNECTED pages for a network (for internal logic)
    getNetworkConnectedCount: state => network => {
      return state.connectedPages[network] ? state.connectedPages[network].length : 0
    },
    // Count SELECTED + PRE-EXISTING pages for a network (for UI display)
    getNetworkSelectedCount: state => network => {
      const pages = state.connectedPages[network] || []
      const selectedIds = state.selectedPageIds[network] || []
      const preExistingIds = state.preExistingPageIds[network] || []
      let count = 0
      pages.forEach(page => {
        const isSelected = selectedIds.includes(page.id) ||
          (page.network_id && selectedIds.includes(page.network_id))
        const isPreExisting = preExistingIds.includes(page.id) ||
          (page.network_id && preExistingIds.includes(page.network_id))
        if (isSelected || isPreExisting) {
          count++
        }
      })
      return count
    },
    getPreExistingPageIds: state => network => {
      return state.preExistingPageIds[network] || []
    },
    getNewlyConnectedPageIds: state => network => {
      return state.newlyConnectedPageIds[network] || []
    },
    isPagePreExisting: state => (network, pageId) => {
      const preExisting = state.preExistingPageIds[network] || []
      return preExisting.includes(pageId)
    }
  }
}
